import { ApiService, ApiUrl, Constants, Enum } from "helpers";
import { AutomationStateType, TriggerTypeClient } from "helpers/enum";
import { action, observable } from "mobx";
import Alert from 'react-s-alert';
import { AutomationReportClientGridFieldName, AutomationReportClientGridColumnName } from "views/Reports/Automation/AutomationReportsGrid/AutomationReportGridConstant";
import moment from 'moment';
import { loadAutomationReportIntoCanvas } from "components/Rappid/RappidCanvasFunctions";

const automationReportClientStore = observable(
  {
    automationId: null,
    automationName: "",
    automationDate: moment.utc(),
    automationState: AutomationStateType.Active,
    automationTotalStep: 1,
    automationCompletion: [],
    automationAudiences: [],
    atsProviderUsers: [],
    atsProviderOffices: [],
    atsProviderGroups: [],
    triggerType: null,
    triggerList: [],
    clientSteps: [],
    reportId: null,
    completionStepName: "",
    completionAutomationName: "",
    clientTriggered: 0,

    gridColumn: [
      {
        id: 1,
        colId: AutomationReportClientGridFieldName.AutomationName,
        field: AutomationReportClientGridFieldName.AutomationName,
        headerName: AutomationReportClientGridColumnName.AutomationName,
        width: 150,
        hide: false,
        order: 0,
      },
      {
        id: 2,
        colId: AutomationReportClientGridFieldName.AutomationCreatedDate,
        field: AutomationReportClientGridFieldName.AutomationCreatedDate,
        headerName: AutomationReportClientGridColumnName.AutomationCreatedDate,
        width: 150,
        hide: false,
        sort: 'desc',
        order: 1,
      },
      {
        id: 3,
        colId: AutomationReportClientGridFieldName.AutomationDescription,
        field: AutomationReportClientGridFieldName.AutomationDescription,
        headerName: AutomationReportClientGridColumnName.AutomationDescription,
        width: 150,
        hide: false,
        order: 2,
      },
      {
        id: 4,
        colId: AutomationReportClientGridFieldName.AutomationStatus,
        field: AutomationReportClientGridFieldName.AutomationStatus,
        headerName: AutomationReportClientGridColumnName.AutomationStatus,
        width: 150,
        hide: false,
        order: 3,
      },
      {
        id: 5,
        colId: AutomationReportClientGridFieldName.AutomationAction,
        field: AutomationReportClientGridFieldName.AutomationAction,
        headerName: AutomationReportClientGridColumnName.AutomationAction,
        width: 150,
        hide: false,
        order: 4,
      },
    ],

    resetAutomationReportStore() {
      this.automationId = null;
      this.automationName = "";
      this.automationState = AutomationStateType.Active;
      this.automationTotalStep = 1;
      this.triggerType = null;
      this.triggerList = [];
      this.clientTriggered = 0;
      this.clientSteps = [];
      this.clientTriggered = 0;
      this.automationAudiences = [];
    },

    setCompletionStepName(completionStepName) {
      this.completionStepName = completionStepName;
    },

    setCompletionAutomationName(completionAutomationName) {
      this.completionAutomationName = completionAutomationName;
    },

    setAutomationTotalStep(automationTotalStep) {
      this.automationTotalStep = automationTotalStep;
    },

    setReportId(reportId) {
      this.reportId = reportId;
    },

    setAutomationCompletion(automationCompletion) {
      this.automationCompletion = [];
      this.automationCompletion = [...automationCompletion];
    },

    setAutomationReportDetail(response) {
      const { arrayStructure, triggerList, id, name, state, createdAt, clientTriggered, clientSteps, audiences, atsProviderUsers, atsProviderGroups, atsProviderOffices } = response.data.result;

      this.automationId = id;
      this.automationName = name;
      this.automationState = state;
      this.automationDate = createdAt;
      this.automationAudiences = audiences;
      this.atsProviderUsers = atsProviderUsers;
      this.atsProviderGroups = atsProviderGroups;
      this.atsProviderOffices = atsProviderOffices;

      if (triggerList) {
        const trigger = JSON.parse(triggerList);
        if (trigger.length > 0) {
          const triggerId = trigger[0].firstColumn.id;
          const placementTriggerIds = [
            Enum.TriggerListClient.ClientPlacementStartDate,
            Enum.TriggerListClient.ClientPlacementEndDate,
            Enum.TriggerListClient.ClientPlacementCreatedDate,
            Enum.TriggerListClient.ClientPlacementStatus,
            Enum.TriggerListClient.ClientPlacementType,
            Enum.TriggerListClient.ClientPlacementCustomFieldList,
            Enum.TriggerListClient.ClientPlacementCustomFieldMultiSelectList,
            Enum.TriggerListClient.ClientPlacementCustomFieldDate,
            // ROI-412: kept in step with automationStore.setTriggerTypeByFirstColumnId, since the alert
            // step's recipients line on the report canvas reads this type.
            Enum.TriggerListClient.ClientPlacementFormScore,
            Enum.TriggerListClient.ClientPlacementRoiAiActivity,
          ];
          const jobTriggerIds = [
            Enum.TriggerListClient.JobStatus,
            Enum.TriggerListClient.ClientJobInterviewDate,
            Enum.TriggerListClient.ClientJobWorkType,
            Enum.TriggerListClient.ClientJobCategories,
            Enum.TriggerListClient.ClientJobTypes,
            Enum.TriggerListClient.ClientJobId,
            Enum.TriggerListClient.ClientJobLocation,
            Enum.TriggerListClient.ClientJobCustomFieldList,
            Enum.TriggerListClient.ClientJobCustomFieldMultiSelectList,
            Enum.TriggerListClient.ClientJobCustomFieldDate,
            Enum.TriggerListClient.ClientJobFormScore,
            Enum.TriggerListClient.ClientJobDateCreated,
            Enum.TriggerListClient.ClientJobDateUpdated,
            Enum.TriggerListClient.ClientJobRoiAiActivity,
          ];
          // Survey score is offered under every type and takes its type from the trigger number.
          this.triggerType = triggerId === Enum.TriggerListClient.SurveyScore
            ? trigger[0].triggerNumber || TriggerTypeClient.ClientBased
            : placementTriggerIds.includes(triggerId)
              ? TriggerTypeClient.PlacementBased
              : jobTriggerIds.includes(triggerId)
                ? TriggerTypeClient.JobBased
                : TriggerTypeClient.ClientBased;
          this.triggerList = [...trigger];
        }
      }

      this.clientTriggered = clientTriggered;
      this.clientSteps = clientSteps && [...clientSteps];

      this.automationTotalStep = JSON.parse(arrayStructure).length;
    },

    getAutomationReportClientDetail(id, expectedGraph) {
      return (ApiService.getDetail(ApiUrl.getAutomationReportClientDetails, id).then(
        action(
          response => {
            if (response && response.status === 200 && response.data && !response.data.isError) {
              this.setAutomationReportDetail(response);
              this.setAutomationReportData(response, true, expectedGraph);
            } else {
              Alert.error(Constants.ALERT_SOMETHING_WENT_WRONG);
            }
            return Promise.resolve(response.data);
          }
        )
      ).catch(
        action(
          err => {
            Alert.error(err);
            return Promise.reject(err);
          }
        )
      ));
    },

    getAutomationReportCompletions(stepId, reportId) {
      const data = {
        stepId,
        reportId,
      };

      return (ApiService.post(ApiUrl.getAutomationReportClientCompletions, data).then(
        action(
          response => {
            if (response && response.status === 200 && response.data && !response.data.isError) {
              this.setAutomationCompletion(response.data.result);
            } else {
              Alert.error(Constants.ALERT_SOMETHING_WENT_WRONG);
            }
            return Promise.resolve(response.data);
          }
        )
      ).catch(
        action(
          err => {
            Alert.error(err);
            return Promise.reject(err);
          }
        )
      ));
    },

    setAutomationReportData(response, redrawCanvas = true, expectedGraph) {
      // Same dead parse as the candidate store: diagramStructure was parsed, defaulted and
      // discarded, with the canvas drawn from arrayStructure and triggerList. Removed for
      // the same reason, the client side carries another 234 MB of that column.
      const { arrayStructure, triggerList } = response.data.result;

      if (redrawCanvas) {
        var trigger = JSON.parse(triggerList);
        loadAutomationReportIntoCanvas(arrayStructure, trigger, expectedGraph, {
          automationType: Enum.AutomationType.Client,
          triggerType: this.triggerType
        });
      }

    },
  }
);

export default automationReportClientStore;
